/* eslint-disable @typescript-eslint/no-unused-vars */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { WhatsappTemplateComponent } from '@src/components/AdminHome/Channels/WhatsApp/WhatsAppTemplates/types';
// eslint-disable-next-line import/no-cycle
import {
  ErrorPayload,
  TemplatedMsgWrapper,
  WhatsAppTemplate,
  WhatsAppTemplateState,
} from '@src/types';

export type WhatsAppTemplatesState = {
  filter: {
    botAccountId: string;
    offset: number;
    limit: number;
    total: number;
    columnOrder: string;
    order: 'asc' | 'desc';
    name?: string;
    states: WhatsAppTemplateState[];
  };
  data: WhatsAppTemplate[];
  contactTemplates: TemplatedMsgWrapper[];
  error?: ErrorPayload;
  loading: ('pending' | 'success' | 'failure' | 'fulfill')[];
};

const initialState: WhatsAppTemplatesState = {
  filter: {
    botAccountId: '',
    columnOrder: 'createdAt',
    order: 'desc',
    limit: 10,
    offset: 0,
    total: 0,
    states: ['ACTIVE', 'INACTIVE', 'PENDING', 'REJECTED'],
  },
  data: [],
  contactTemplates: [],
  loading: [],
};

const whatsAppTemplatesSlice = createSlice({
  name: 'whatsAppTemplates',
  initialState,
  reducers: {
    fetchWhatsAppTemplatesByBotAccount(
      state,
      action: PayloadAction<{
        filter: WhatsAppTemplatesState['filter'];
      }>
    ) {
      state.loading = ['pending'];
      state.filter = action.payload.filter;
    },
    fetchWhatsAppTemplatesByBotAccountSuccess(
      state,
      action: PayloadAction<{ total: number; data: WhatsAppTemplate[] }>
    ) {
      state.filter = {
        ...state.filter,
        total: action.payload.total,
      };
      action.payload.data.forEach(addTemplateComponentRecordProperty);
      state.data = action.payload.data;
      state.loading = ['success'];
    },
    fetchWhatsAppTemplatesByBotAccountFailure(
      state,
      action: PayloadAction<{ error: ErrorPayload }>
    ) {
      state.data = [];
      state.error = action.payload.error;
      state.loading = ['failure'];
    },
    fetchWhatsAppTemplatesByBotAccountFulfill(state) {
      state.loading = [
        ...state.loading.filter(v => v !== 'pending'),
        'fulfill',
      ];
    },
    updateWhatsAppTemplateByBotAccountStatus(
      state,
      action: PayloadAction<{
        accountId: number;
        name: string;
        status: WhatsAppTemplateState;
      }>
    ) {
      const { accountId, name, status } = action.payload;

      state.data.find((template, index) => {
        if (template.botAccountId === accountId && template.name === name) {
          state.data[index].status = status;
          return true;
        }
        return false;
      });
    },
    fetchWhatsAppTemplatesByContactId(
      state,
      _: PayloadAction<{ showCallPermissionOnly?: boolean } | undefined>
    ) {
      state.loading = ['pending'];
    },
    fetchWhatsAppTemplatesByContactIdSuccess(
      state,
      action: PayloadAction<TemplatedMsgWrapper[]>
    ) {
      state.contactTemplates = action.payload;
      state.loading = ['success'];
    },
    fetchWhatsAppTemplatesByContactIdFailure(
      state,
      action: PayloadAction<{ error: ErrorPayload }>
    ) {
      state.contactTemplates = [];
      state.error = action.payload.error;
      state.loading = ['failure'];
    },
    fetchWhatsAppTemplatesByContactIdFulfill(state) {
      state.loading = [
        ...state.loading.filter(v => v !== 'pending'),
        'fulfill',
      ];
    },
    setWhatsAppTemplateFilter(
      state,
      action: PayloadAction<Partial<WhatsAppTemplatesState['filter']>>
    ) {
      state.filter = { ...state.filter, ...action.payload };
    },
  },
});

export default whatsAppTemplatesSlice.reducer;

export const {
  fetchWhatsAppTemplatesByBotAccount,
  fetchWhatsAppTemplatesByBotAccountSuccess,
  fetchWhatsAppTemplatesByBotAccountFailure,
  fetchWhatsAppTemplatesByBotAccountFulfill,
  updateWhatsAppTemplateByBotAccountStatus,
  fetchWhatsAppTemplatesByContactId,
  fetchWhatsAppTemplatesByContactIdSuccess,
  fetchWhatsAppTemplatesByContactIdFailure,
  fetchWhatsAppTemplatesByContactIdFulfill,
  setWhatsAppTemplateFilter,
} = whatsAppTemplatesSlice.actions;

export function addTemplateComponentRecordProperty(item: WhatsAppTemplate) {
  item.components = item.templateComponent as WhatsappTemplateComponent[];
  item.templateComponentRecord = {};
}
